/**
 * DriveZone — LocationInput
 * Input with Places Autocomplete dropdown.
 * Restricted to US cities. Used in both search panels.
 */

import { MapPin, X } from 'lucide-react'
import { usePlacesAutocomplete } from '@/lib/usePlacesAutocomplete'

interface LocationInputProps {
  placeholder?: string
  onSelect: (description: string) => void
  value?: string
  onChange?: (v: string) => void
  disabled?: boolean
  autoFocus?: boolean
}

export default function LocationInput({
  placeholder = 'Enter a US city…',
  onSelect,
  value: controlledValue,
  onChange,
  disabled,
  autoFocus,
}: LocationInputProps) {
  const {
    inputRef, value, setValue, predictions,
    showDropdown, handleChange, handleBlur, selectPrediction,
  } = usePlacesAutocomplete({ onSelect })

  // Sync controlled value into local state when parent clears it
  const displayValue = controlledValue !== undefined ? controlledValue : value

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    handleChange(e)
    onChange?.(e.target.value)
  }

  function handleClear() {
    setValue('')
    onChange?.('')
    onSelect('')
    inputRef.current?.focus()
  }

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          className="input pl-9 pr-8"
          placeholder={placeholder}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
        />
        {displayValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            tabIndex={-1}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Predictions dropdown */}
      {showDropdown && predictions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white rounded-xl border border-slate-200
                        shadow-modal py-1 max-h-52 overflow-y-auto">
          {predictions.map((pred) => (
            <li key={pred.place_id}>
              <button
                type="button"
                className="w-full text-left px-3 py-2.5 text-sm text-slate-700
                           hover:bg-slate-50 flex items-center gap-2"
                onMouseDown={(e) => { e.preventDefault(); selectPrediction(pred) }}
              >
                <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="truncate">{pred.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
